/**
 * TerraMatrix Universal Data Platform - HistoryPanel Component
 *
 * Sprint 5: Frontend - Data Page
 * Created: November 2025
 *
 * Side panel showing query history.
 */

import React, { useState, useEffect } from 'react';
import { QueryHistorySchema } from '@/schemas/persistence';

interface HistoryItem {
  id: string;
  query: string;
  timestamp: Date;
  source?: string;
}

interface HistoryPanelProps {
  onSelect: (query: string) => void;
  onClose: () => void;
}

// Local storage key for history
const HISTORY_STORAGE_KEY = 'terramatrix_query_history';
const MAX_HISTORY_ITEMS = 50;

export const HistoryPanel: React.FC<HistoryPanelProps> = ({ onSelect, onClose }) => {
  const [history, setHistory] = useState<HistoryItem[]>([]);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (stored) {
        const raw: unknown = JSON.parse(stored);
        const result = QueryHistorySchema.safeParse(raw);
        if (!result.success) {
          console.warn('Query history failed validation:', result.error);
          return;
        }
        // Convert timestamp strings back to Date objects
        setHistory(
          result.data.map((item) => ({
            id: item.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            query: item.query,
            timestamp: new Date(item.timestamp ?? Date.now()),
            source: undefined,
          }))
        );
      }
    } catch (error) {
      console.error('Failed to load query history:', error);
    }
  }, []);

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem(HISTORY_STORAGE_KEY);
  };

  const removeItem = (id: string) => {
    const updated = history.filter((item) => item.id !== id);
    setHistory(updated);
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updated));
  };

  // Group history by date
  const groupedHistory = React.useMemo(() => {
    const groups: Record<string, HistoryItem[]> = {};

    history.forEach((item) => {
      const date = item.timestamp.toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(item);
    });

    return groups;
  }, [history]);

  const dateLabels = Object.keys(groupedHistory).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  );

  return (
    <div className="w-80 border-l border-border bg-card flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="font-semibold flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Query History
        </h2>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors p-1"
          aria-label="Close history panel"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* History List */}
      <div className="flex-1 overflow-y-auto">
        {history.length === 0 ? (
          <div className="text-center text-muted-foreground py-8 px-4">
            <svg
              className="w-12 h-12 mx-auto mb-4 opacity-50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p>No query history yet</p>
            <p className="text-sm mt-2">Your queries will appear here</p>
          </div>
        ) : (
          dateLabels.map((date) => (
            <div key={date}>
              <div className="sticky top-0 bg-muted/50 px-4 py-2 text-xs font-medium text-muted-foreground">
                {date === new Date().toLocaleDateString() ? 'Today' : date}
              </div>
              <div className="p-2 space-y-1">
                {groupedHistory[date].map((item) => (
                  <div
                    key={item.id}
                    className="group flex items-start gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                    onClick={() => onSelect(item.query)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && onSelect(item.query)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{item.query}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeItem(item.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity p-1"
                      aria-label="Remove from history"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Clear History Button */}
      {history.length > 0 && (
        <div className="p-4 border-t border-border">
          <button
            onClick={clearHistory}
            className="w-full py-2 text-sm text-muted-foreground hover:text-destructive
                       transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Clear History
          </button>
        </div>
      )}
    </div>
  );
};

// Helper function to save query to history (to be called from useDataQuery)
export const saveQueryToHistory = (query: string, source?: string) => {
  try {
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
    let history: HistoryItem[] = [];
    if (stored) {
      const raw: unknown = JSON.parse(stored);
      const result = QueryHistorySchema.safeParse(raw);
      if (result.success) {
        history = result.data.map((item) => ({
          id: item.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          query: item.query,
          timestamp: new Date(item.timestamp ?? Date.now()),
          source: undefined,
        }));
      }
    }

    // Add new item at the beginning
    const newItem: HistoryItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      query,
      timestamp: new Date(),
      source,
    };

    // Deduplicate and limit
    const filtered = history.filter((item) => item.query !== query);
    const updated = [newItem, ...filtered].slice(0, MAX_HISTORY_ITEMS);

    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Failed to save query to history:', error);
  }
};

export default HistoryPanel;
